import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import ProxyManager from '@/components/admin/ProxyManager';
import UserManagement from '@/components/admin/UserManagement';
import Statistics from '@/components/admin/Statistics';
import SetupModeration from '@/components/admin/SetupModeration';
import TradingMonitor from '@/components/admin/TradingMonitor';
import { Shield, AlertCircle } from 'lucide-react';

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    } else if (user) {
      checkAdminAccess();
    }
  }, [user, loading, navigate]);

  const checkAdminAccess = async () => {
    try {
      await axios.get(`${backendUrl}/api/admin/check`, {
        withCredentials: true
      });
      setIsAdmin(true);
    } catch (error) {
      console.error('Not an admin:', error);
      setIsAdmin(false);
      toast.error('У вас нет прав администратора');
      setTimeout(() => navigate('/'), 2000);
    } finally {
      setChecking(false);
    }
  };

  if (loading || checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Загрузка...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Card className="bg-[#1a1a1a] border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-400">
              <AlertCircle className="h-6 w-6" />
              <p>Доступ запрещён</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-[#00ff88]" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Панель администратора</h1>
          </div>
          <p className="text-gray-400">Управление системой и пользователями</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="proxies" className="w-full">
          <TabsList className="bg-[#1a1a1a] border border-gray-800 mb-6">
            <TabsTrigger value="proxies" className="data-[state=active]:bg-[#00ff88] data-[state=active]:text-black">
              Прокси
            </TabsTrigger>
            <TabsTrigger value="trading" className="data-[state=active]:bg-[#00ff88] data-[state=active]:text-black">
              Торговля
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#00ff88] data-[state=active]:text-black">
              Пользователи
            </TabsTrigger>
            <TabsTrigger value="setups" className="data-[state=active]:bg-[#00ff88] data-[state=active]:text-black">
              Сетапы
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#00ff88] data-[state=active]:text-black">
              Статистика
            </TabsTrigger>
          </TabsList>

          <TabsContent value="proxies">
            <ProxyManager />
          </TabsContent>

          <TabsContent value="trading">
            <TradingMonitor />
          </TabsContent>

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          <TabsContent value="setups">
            <SetupModeration />
          </TabsContent>

          <TabsContent value="stats">
            <Statistics />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
